import { Injectable } from '@angular/core';
import { webSocket, WebSocketSubject } from 'rxjs/webSocket';
import { Observable, Subject, BehaviorSubject } from 'rxjs';
import { global } from '../global';
import { ConnectionStatusService } from './connection-status.service';

@Injectable({
  providedIn: 'root'
})
export class CompaniaWebsocketService {
  private socket$: WebSocketSubject<any>;
  private companiaSubject: Subject<any> = new Subject();
  private reconnectionAttempts = 0;
  private readonly RECONNECT_INTERVAL = 5000;
  private readonly MAX_RECONNECT_ATTEMPTS = 5;
  private currentConnectionUrl: string | null = null;
  private isConnectedSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private gaveUp = false;
  private socketAbierto = false;

  constructor(private connectionStatusService: ConnectionStatusService) {
    this.connectionStatusService.isBackendDown$.subscribe((isDown) => {
      if (!isDown && this.gaveUp && this.currentConnectionUrl) {
        this.gaveUp = false;
        this.reconnectionAttempts = 0;
        this.establishConnection(this.currentConnectionUrl);
      }
    });
  }

  public connect(idnectum: number, options?: { metaapi?: boolean; timeframe?: string }): void;
  public connect(idnectums: string): void;
  public connect(input: number | string, options?: { metaapi?: boolean; timeframe?: string }): void {
    this.gaveUp = false;
    this.reconnectionAttempts = 0;
    if (typeof input === 'number') {
      this.connectSingle(input, options);
    } else {
      this.connectMultiple(input);
    }
  }

  private connectSingle(idnectum: number, options?: { metaapi?: boolean; timeframe?: string }): void {
    if (options?.metaapi === true) {
      const timeframe = encodeURIComponent(options.timeframe ?? 'D');
      this.establishConnection(`${global.wsUrl}ws/companiametaapi/${timeframe}/${idnectum}`);
      return;
    }
    this.establishConnection(`${global.wsUrl}ws/compania/${idnectum}`);
  }

  private connectMultiple(idnectums: string): void {
    this.establishConnection(this.urlCompanias(idnectums));
  }

  private urlCompanias(idnectums: string): string {
    return `${global.wsUrl}ws/companias?ids=${idnectums}`;
  }

  /**
   * true si el socket sigue abierto para exactamente estos ids. Gestor/Broker lo consultan para
   * no cerrar y reabrir la misma conexión con cada lote del recálculo.
   */
  public conectadoA(idnectums: string): boolean {
    return this.socketAbierto && !this.gaveUp && this.currentConnectionUrl === this.urlCompanias(idnectums);
  }

  private establishConnection(url: string): void {
    console.log('Connecting to WebSocket URL:', url);
    this.currentConnectionUrl = url;
    // Cerrar conexión existente si hay una diferente
    if (this.socket$ && !this.socket$.closed) {
      this.socket$.complete();  // Cerrar conexión anterior
    }

    const socket = webSocket(url);
    this.socket$ = socket;
    this.socketAbierto = true;

    // El cierre de un socket ya sustituido no debe marcar como cerrado el actual.
    socket.subscribe(
      (data) => {
        this.companiaSubject.next(data);
        this.isConnectedSubject.next(true);
        this.reconnectionAttempts = 0;
      },
      (error) => {
        console.error('WebSocket error:', error);
        if (this.socket$ === socket) this.socketAbierto = false;
        this.isConnectedSubject.next(false);
        this.reconnect();
      },
      () => {
        console.log('WebSocket connection closed');
        if (this.socket$ === socket) this.socketAbierto = false;
        this.isConnectedSubject.next(false);
      }
    );
  }

  public disconnect(): void {
    this.gaveUp = false;
    this.socketAbierto = false;
    this.currentConnectionUrl = null;
    if (this.socket$) {
      this.socket$.complete();
    }
    this.isConnectedSubject.next(false);

  }

  public getCompaniaUpdates(): Observable<any> {
    return this.companiaSubject.asObservable();
  }

  private reconnect(): void {
    this.reconnectionAttempts++;
    if (this.reconnectionAttempts < this.MAX_RECONNECT_ATTEMPTS) {
      setTimeout(() => {
        console.log(`Attempting reconnection #${this.reconnectionAttempts}`);
        if (this.currentConnectionUrl) {
          this.establishConnection(this.currentConnectionUrl);
        }
      }, this.RECONNECT_INTERVAL);
    } else {
      console.error('Max reconnection attempts reached');
      this.gaveUp = true;
      this.connectionStatusService.reportFailure();
    }
  }
  public isConnected(): Observable<boolean> {
    return this.isConnectedSubject.asObservable();
  }
}
